/* eslint-disable no-console */

import {
  Button,
  Link,
  ThemeProvider,
  theme,
  Spinner,
} from '@everlywell/ui-kit';
import { CartProvider } from 'components/Cart';
import CartDrawer from 'components/Cart/components/CartDrawer';
import RemoveOutOfStockProducts from 'components/Cart/components/RemoveOutOfStockProducts';
import RemoveQuestProducts from 'components/Cart/components/RemoveQuestProducts';
import NavigationBarV2 from 'components/NavigationBarV2';
import PurchaseOptionsOverlayProvider from 'components/PurchaseOptionsDrawerProvider';
import { Theme, ThemeContext } from 'components/Themable';
import { ToastProvider } from 'components/ToastContext/ToastContext';
import Cookies from 'js-cookie';
import React, {
  createContext,
  lazy,
  Suspense,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { ThemeProvider as OldThemeProvider } from 'styled-components';
import { ariesTheme as oldTheme } from 'styles/ariesTheme';
import GlobalStyle from 'styles/global';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';
import { CookieNames } from 'utils/constants/cookies';
import { QUERY_PARAMS } from 'utils/constants/queryParams';
import { hashSha256, isBrowser } from 'utils/helpers';
import { updateCJEvent } from 'utils/helpers/marketing/cj';
import { UserContext } from 'utils/hooks/useUserContext/context';
import { gtm } from 'utils/trackers';
import { ProductsBySlugType } from 'utils/types';

import { NotificationContextContainer } from '../../containers/Notification';
import * as S from './styles';

declare global {
  interface Window {
    appRoot: string;
    asset_host: string;
  }
}

const mainElement = 'main';

const FooterV2 = lazy(() => import('components/FooterV2'));

type Props = {
  children: React.ReactNode;
  productsBySlug?: ProductsBySlugType;
  viewEventId?: string;
  productId?: number;
  footerRef?: React.Ref<HTMLDivElement>;
  showFDADisclaimer?: boolean;
  showAvailableForSelectConditionsDisclaimer?: boolean;
  showMedicareDisclaimer?: boolean;
  isV2Page?: boolean;
  noNavbar?: boolean;
};

export const ProductInfoContext = createContext<ProductsBySlugType>({});

export const NEW_NAV_HEIGHT = ['73px', null, null, '89px'];

const Layout = (props: Props): JSX.Element => {
  const mainElementRef = useRef<HTMLDivElement>(null);

  const [cJEventParam] = useQueryParam('cjevent', StringParam);
  const [campaignId] = useQueryParam(
    QUERY_PARAMS.MARKETING.campaignId,
    NumberParam,
  );
  const [enableStripeUpgradeQueryParam] = useQueryParam(
    'enableStripeUpgrade',
    StringParam,
  );
  const {
    userData: { fetched, userId, userEmail },
  } = useContext(UserContext);
  const {
    productsBySlug = {},
    showFDADisclaimer = false,
    isV2Page = false,
    noNavbar = false,
  } = props;

  useEffect(() => {
    cJEventParam && updateCJEvent(cJEventParam);

    if (campaignId) {
      Cookies.set(CookieNames.IterableEmailCampaignId, campaignId.toString());
    }
  }, [cJEventParam, campaignId]);

  useEffect(() => {
    // track user page view in GTM.
    // return the hashed email and user id for logged in users, and undefined for non-logged in users
    const trackUserPageView = async (
      email: string | undefined,
      id: number | undefined,
    ) => {
      isBrowser() &&
        gtm.track({
          event: 'pageview',
          pageview: {
            hashedEmail: hashSha256(email),
            userId: id,
          },
        });
    };
    if (fetched) {
      trackUserPageView(userEmail!, userId === null ? undefined : userId);
    }
  }, [userEmail, userId, fetched]);

  useEffect(() => {
    if (!isBrowser()) return;

    const cookie = window.freshpaint?.cookie;

    console.debug('[Freshpaint Cookie]', cookie);

    if (cookie?.props?.distinct_id) {
      console.debug('[Freshpaint distinct_id]', cookie.props.distinct_id);
    } else {
      console.info('[Freshpaint] No distinct_id found yet');
    }

    if (cookie?.props?.$session_id) {
      console.debug('[Freshpaint session_id]', cookie.props.$session_id);
    }

    if (fetched && userId && userEmail) {
      console.debug('[Freshpaint Identify Debug]', {
        userId,
        email: userEmail,
      });
    } else if (fetched) {
      console.debug('[Freshpaint] User is anonymous or missing ID/email');
    }
  }, [fetched, userId, userEmail]);

  useEffect(() => {
    if (enableStripeUpgradeQueryParam) {
      sessionStorage.setItem(
        'enableStripeUpgrade',
        enableStripeUpgradeQueryParam,
      );
    }
  }, [enableStripeUpgradeQueryParam]);

  const handleSkipToContentClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    event.preventDefault();
    if (mainElementRef.current) {
      mainElementRef.current.setAttribute('tabIndex', '-1');
      mainElementRef.current.focus();
      mainElementRef.current.removeAttribute('tabIndex');
    }
  };

  return (
    <div id="app" className="siteRoot">
      <ThemeProvider theme={theme}>
        <OldThemeProvider theme={oldTheme}>
          <ThemeContext.Provider value={Theme.Default}>
            <ProductInfoContext.Provider value={productsBySlug}>
              <ToastProvider>
                <CartProvider>
                  <PurchaseOptionsOverlayProvider>
                    <Link
                      href={`#${mainElement}`}
                      position="fixed"
                      display="block"
                      zIndex={-1}
                      tabIndex={0}
                      top="-10000px"
                      opacity={0}
                      left="50%"
                      transform="translateX(-50%)"
                      color="tints.white"
                      textDecoration="none"
                      as={Button}
                      aria-label="Skip to main content"
                      sx={{
                        '&:not(:focus-visible)': {
                          top: '-10000px !important',
                          opacity: 0,
                          zIndex: -1,
                        },
                        '&:focus-visible': {
                          top: ['72px !important', null, '64px !important'], // Skip header
                          opacity: 1,
                          zIndex: 2000, // Skip all based on chakra ui values
                          transition: 'opacity 0.2s ease-in, top 0.2s ease-in',
                        },
                      }}
                      _hover={{
                        textDecoration: 'none',
                      }}
                      onClick={handleSkipToContentClick}
                    >
                      Skip to content
                    </Link>

                    {noNavbar ? null : <NavigationBarV2 />}

                    <S.Layout
                      role="main"
                      id={mainElement}
                      isV2Page={isV2Page}
                      ref={mainElementRef}
                    >
                      <NotificationContextContainer>
                        {/* // TODO: IN2-1602. Check if new nav is on to update add a margin to the page */}
                        {props.children}
                      </NotificationContextContainer>
                    </S.Layout>
                    <Suspense fallback={<Spinner />}>
                      <FooterV2 showFDADisclaimer={showFDADisclaimer} />
                    </Suspense>
                    {/*
                  NOTE:
                  There was an issue were the CartDrawer was being rendered multiple times when it was part of the CartContainer component.
                  This makes sure the  is rendered here to ensure it is rendered once and only once.
                 */}
                    <CartDrawer />
                  </PurchaseOptionsOverlayProvider>
                  <RemoveOutOfStockProducts />
                  <RemoveQuestProducts />
                </CartProvider>
              </ToastProvider>
            </ProductInfoContext.Provider>
          </ThemeContext.Provider>
        </OldThemeProvider>
      </ThemeProvider>

      <GlobalStyle />
    </div>
  );
};

export default Layout;
